import { Controller } from '@hotwired/stimulus'
import Rails from '../../rails/rails-ujs'
import Text from '../../lib/text'

export default class extends Controller {
  static targets = ['route', 'step', 'progressBar', 'progressCounter', 'progressCurrent', 'progressTotal', 'savingAnimation', 'savingExplanation', 'notice']

  connect () {
    this.initRoute()
    this.initEventHandlers()
    this.initHistory()

    this.extractCyclabeSteps()
  }

  disconnect () {
    window.removeEventListener('popstate', this.popHistoryCallback)
    document.removeEventListener('keypress', this.keypressCallback)

    clearInterval(this.checkSaved)
  }

  /* === initialization === */
  initRoute () {
    this.steps = this.stepTargets
    this.flags = new Array(this.stepTargets.length).fill(true)

    for (let i = 1; i < this.steps.length; i++) {
      this.removeStep(i)
      this.finalStepIndex = this.steps[i].dataset.stepIndex
    }

    this.currentStep = this.steps[0]
    this.markStepAsVisited(this.currentStep)
    this.scrollLeft = 0
    this.index = 0
  }

  initHistory () {
    if (this.element.closest('.modal')) return // Dynaforms shown in a modal do not have navigation

    this.popHistoryCallback = this.popHistory.bind(this)
    window.addEventListener('popstate', this.popHistoryCallback)

    this.pushHistory()
  }

  initEventHandlers () {
    this.keypressCallback = this.submitStep.bind(this)
    document.addEventListener('keypress', this.keypressCallback)

    Rails.delegate(this.routeTarget, 'input[type=text]', 'input', this.clearErrors.bind(this))
    Rails.delegate(this.routeTarget, 'input[type=radio]', 'change', this.clearErrors.bind(this))
  }

  extractCyclabeSteps () {
    this.cyclableSteps = []

    for (let i = 1; i < this.steps.length; i++) {
      if (this.steps[i].dataset.cyclable === 'true') {
        this.cyclableSteps.push(this.steps[i])

        this.steps.splice(i, 1)
        this.flags.splice(i, 1)
        i--
      }
    }
  }

  /* === user actions === */
  next (event, validate = true) {
    if (validate && !this.validate(event)) return

    this.index = this.updateRoute(event.target.dataset.route)
    this.pushHistory()

    if (this.isFinalStep) this.loadingPleaseWait()
  }

  skip (event) {
    this.next(event, false)
  }

  previous () {
    history.back()
  }

  close () {
    Turbo.visit(this.data.get('closeUrl'), { action: 'replace' })
  }

  cycle (event) {
    const target = event.currentTarget
    const route = target.dataset.route.split(' ')

    let i = 1
    this.cyclableSteps.forEach(step => {
      if (route.includes(step.dataset.stepIdentifier)) {
        this.flags.splice(this.index + i, 0, false)
        this.steps.splice(this.index + i, 0, step.cloneNode(true))
        i++
      }
    })

    this.next(event)
  }

  /* === form display === */
  mountRoute (event) {
    const target = event.currentTarget
    const routeGroup = target.dataset.routeGroup

    this.steps.forEach((step, _) => {
      if (step.dataset.stepIdentifier.indexOf(routeGroup) !== -1) {
        this.finalStepIndex = step.dataset.stepIndex
        target.dataset.route = target.dataset.route || step.dataset.stepIdentifier

        this.setParamsToDisplay(step)
      }
    })

    this.next(event)
  }

  setParamsToDisplay (step) {
    step.querySelectorAll('input[data-display]').forEach(e => {
      const text = e.dataset.value || e.value

      this.updateDisplay({
        target: {
          dataset: {
            value: text,
            display: e.dataset.display
          }
        }
      })
    })
  }

  updateRoute (route) {
    if (route) {
      const stepsInRoute = route.split(' ')
      for (let i = this.index + 1; i < this.steps.length; i++) {
        const step = this.steps[i]

        this.removeStep(i)
        this.flags[i] = stepsInRoute.includes(step.dataset.stepIdentifier)
      }
    }

    return this.addStep()
  }

  addStep () {
    const newIndex = this.flags.findIndex((flag, index) => {
      return flag && index > this.index
    })

    const step = this.steps[newIndex]
    this.markStepAsVisited(step)
    if (!this.hasStep(step)) this.routeTarget.appendChild(step)

    return newIndex
  }

  removeStep (index) {
    // Remove a step but allow it to be used again
    const step = this.steps[index]
    if (!this.hasStep(step)) return

    this.flags[index] = false
    this.routeTarget.removeChild(this.steps[index])
  }

  hasStep (step) {
    return !!step.parentNode
  }

  showCurrentStep () {
    if (this.stepTargets.length === 1) return

    this.steps.forEach((el, i) => {
      setTimeout(() => { // Move this out of the current thread so that animations work properly
        el.classList.toggle('dynaform__step--active', this.index === i)
        el.classList.toggle('dynaform__step--done', this.index > i)
      }, 10)

      if (this.index === i) this.currentStep = el
    })
  }

  markStepAsVisited (step) {
    try {
      const identifier = step.dataset.stepIdentifier
      const index = step.dataset.stepIndex

      fetch(`/analytics/click/fake-id/${index}-${identifier}`, {
        method: 'GET',
        keepalive: true
      })
    } catch (e) {
      // Silently ignore any fetch errors
    }
  }

  trackEvent (event) {
    try {
      fetch(`/analytics/click/fake-id/${event}`, {
        method: 'GET',
        keepalive: true
      })
    } catch (e) {
      // Silently ignore any fetch errors
    }
  }

  showCurrentProgress () {
    const progress = this.currentStepIndex / this.finalStepIndex * 100

    this.progressBarTarget.style.width = `${progress}%`
    this.progressCurrentTarget.textContent = this.currentStepIndex
    this.progressTotalTarget.textContent = `/ ${this.finalStepIndex}`
    this.element.classList.toggle('dynaform--start', this.isFirstStep)
    this.element.classList.toggle('dynaform--end', this.isFinalStep)
  }

  loadingPleaseWait () {
    this.submitTheForm()

    setTimeout(() => {
      this.trackEvent('pooling')
      this.checkSaved = setInterval(() => {
        if (!this.data.get('saved')) return

        clearInterval(this.checkSaved)
        Turbo.visit(this.data.get('segueUrl'), { action: 'replace' })
      }, 100)
    }, this.data.get('waitAfterSaving'))
  }

  submitTheForm () {
    this.trackEvent(`submitting-${this.data.get('loggedIn')}`)
    const form = document.getElementById('receptions-form')

    if (form.disabled) {
      this.trackEvent('form-disabled')
      form.submit()
    } else if (!form.requestSubmit) {
      this.trackEvent('form-submit')
      form.submit()
    } else {
      this.trackEvent('form-requestSubmit')
      form.requestSubmit()
    }
  }

  /* === browser back button === */
  pushHistory () {
    const data = { index: this.index }
    const title = this.currentStep.querySelector('.dynaform__explanation, .dynaform__question, h1').textContent.replace(/\s+/g, ' ').trim()
    this.index === 0 ? history.replaceState(data, title) : history.pushState(data, title)
  }

  popHistory (e) {
    // Allow normal browser navigation if no state is recorded
    if (e.state.index === undefined) return

    // Reject forward navigation if step is invalid
    if (this.index < e.state.index && !this.validate()) return

    this.index = e.state.index
  }

  /* === special controls === */
  fillOptionsInput (e) {
    const text = e.target.textContent
    const input = this.currentStep.querySelector('input')

    input.value = text
    Rails.fire(input, 'change')
  }

  showOptionsInput () {
    this.currentStep.querySelector('.dynaform__actions .dynaform__button').classList.remove('hidden')

    const input = this.currentStep.querySelector('.dynaform__field input')
    input.classList.remove('hidden')
    input.focus()
  }

  updateDisplay (e) {
    const text = e.target.dataset.value || e.target.value
    const target = e.target.dataset.display

    this.steps.forEach(step => {
      step.querySelectorAll(`[data-action-target='${target}']`).forEach(el => {
        const prefix = el.dataset.prefix || ''
        const suffix = el.dataset.suffix || ''

        el.textContent = `${prefix}${text}${suffix}`
      })
    })
  }

  /* === validations === */
  validate (e = null) {
    if (e) this.scrappableStep(e)
    return this.validateFields() & this.validateTogglers()
  }

  validateFields () {
    const field = this.currentStep.querySelector('.dynaform__field')
    if (!field) return true

    const valid = Text.isPresent(field.querySelector('input[type=text]').value)
    field.classList.toggle('dynaform__field--error', !valid)
    return valid
  }

  validateTogglers () {
    const toggler = this.currentStep.querySelector('.dynaform__toggler')
    if (!toggler) return true

    const valid = !!toggler.querySelector('.dynaform__toggler input:checked')
    toggler.classList.toggle('dynaform__toggler--error', !valid)
    return valid
  }

  validateNameLength (e) {
    const inputs = this.currentStep.querySelectorAll('.dynaform__field input')
    const valid = Array.prototype.every.call(inputs, (input) => input.value.length < 17)
    this.noticeTarget.classList.toggle('hidden', valid)
  }

  /* === event handlers === */
  submitStep (e) {
    if (e.which === 13) {
      const submit = this.currentStep.querySelector('.dynaform__actions [data-action~="dynaform#next"]:not(.hidden)')
      if (submit) {
        document.activeElement.blur()
        Rails.fire(submit, 'click')
      }

      e.preventDefault()
      return false
    }
  }

  clearErrors (e) {
    const field = e.target.closest('.dynaform__field--error')
    field && field.classList.remove('dynaform__field--error')

    const toggler = e.target.closest('.dynaform__toggler--error')
    toggler && toggler.classList.remove('dynaform__toggler--error')
  }

  /* === getters/setters === */
  get index () {
    return parseInt(this.data.get('index'))
  }

  set index (value) {
    this.data.set('index', value)

    this.showCurrentStep()
    this.showCurrentProgress()
  }

  get isFirstStep () {
    return this.currentStepIndex === 0
  }

  get isFinalStep () {
    return this.currentStepIndex === this.finalStepIndex
  }

  get currentStepIndex () {
    return Number(this.currentStep.dataset.stepIndex)
  }

  get finalStepIndex () {
    return Number(this.routeTarget.dataset.steps)
  }

  set finalStepIndex (value) {
    this.routeTarget.dataset.steps = value
  }

  /* === scrapping steps === */
  scrappableStep (e) {
    if (this.currentStep.dataset.stepIdentifier !== 'wedding_gift_registry_external_list') return

    const routes = e.target.dataset.routes.split(',')
    const url = document.getElementById('event_gift_registry_external_list_url')
    if (url.value && (url.value.includes('querodecasamento') || url.value.includes('finalfeliz.de'))) {
      e.target.dataset.route = routes[0]
    } else {
      e.target.dataset.route = routes[1]
    }
  }
}
